/**
 * scenario-galaxy-shape.ts — galaxy-shape generators (v4M2.2).
 *
 * Authoritative spec: `docs/GALAXY.md §3.8`. The four shapes drive
 * system positions, jump-graph topology, and homeworld preferences.
 * Each generator is pure over a Mulberry32 RNG: same `(shape, seed,
 * systemCount)` triple → identical galaxy layout every time.
 *
 * Implementation seam: `generateGalaxyByShape` returns a
 * `ScenarioGalaxy` record. The scenario-init pass in `scenario.ts`
 * iterates `positions`, allocates one `StarSystem` per slot, and
 * reads `flags[i]` for per-system seeding overrides (biome bias on
 * the Bottleneck choke, etc.). When `adjacency` is non-null, the
 * jump-graph wiring uses it directly; when null, the legacy MST +
 * extras pass in `jump-graph.ts` runs unchanged.
 *
 * The `homeworldPreferences` array, when present, gives the
 * empire-placement step a per-empire system index. When null, the
 * legacy `systems[e * floor(N/E)]` fallback applies.
 */

import type {
  BiomeKind,
  GalaxyShape,
  GameState,
} from '../state/types';
import { nextFloat, nextInt } from '../state/rng';

/**
 * Per-system override hints emitted by a generator. Currently
 * carries:
 *   - `forceBiomeBias` — Bottleneck choke flags `'gaia'` so the
 *     planet-roll pass weights heavily toward Gaia.
 *   - `forcePlanetCount` — Bottleneck choke gets exactly 3 planets
 *     regardless of `planetsPerSystemTier`.
 *
 * Extend by adding more optional fields when new shape mechanics
 * land — keep this interface narrow so the scenario-init pass's
 * read sites stay readable.
 */
export interface SystemFlags {
  forceBiomeBias?: BiomeKind;
  forcePlanetCount?: number;
}

/**
 * What a generator returns to the scenario-init pass. Positions
 * align with `flags`, both of length `systemCount`. `adjacency`
 * and `homeworldPreferences` may be `null` to opt back into the
 * legacy fallback paths.
 */
export interface ScenarioGalaxy {
  positions: ReadonlyArray<{ x: number; y: number }>;
  flags: ReadonlyArray<SystemFlags | null>;
  /**
   * Explicit adjacency. `null` → fall back to the distance-based
   * MST + extras in `jump-graph.ts:wireJumpGraph`. Non-null →
   * each `[i, j]` is a system-pair link to wire as a JP edge.
   */
  adjacency: ReadonlyArray<readonly [number, number]> | null;
  /**
   * Per-empire preferred starting-system indices. `null` → fall
   * back to `systems[e * floor(N/E)]`. Non-null → element `e` is
   * the system index for empire `e` (in the empire-roster order
   * `scenario.ts` constructs).
   */
  homeworldPreferences: ReadonlyArray<number> | null;
}

// ---------------------------------------------------------------------------
// Public dispatcher
// ---------------------------------------------------------------------------

/**
 * v4M2.2 — entry point. Dispatches to the four shape implementations
 * based on `shape`; same `state.rng` instance drives every random
 * draw for determinism.
 *
 * `systemCount` is the global system-count slider value
 * (`§3.3` 6..30 range). Per-shape minimums are enforced inside the
 * relevant generator; if a too-low count is passed, the generator
 * adjusts (e.g. Bottleneck never produces fewer than 2 systems per
 * cluster + 1 choke = 5, padded to the global min of 6).
 */
export function generateGalaxyByShape(
  state: GameState,
  shape: GalaxyShape,
  systemCount: number,
): ScenarioGalaxy {
  switch (shape) {
    case 'even':
      return generateEven(state, systemCount);
    case 'spiral':
      return generateSpiral(state, systemCount);
    case 'bottleneck':
      return generateBottleneck(state, systemCount);
    case 'core-and-rim':
      return generateCoreAndRim(state, systemCount);
  }
}

// ---------------------------------------------------------------------------
// Even — uniform-jitter placement (v1 default behaviour)
// ---------------------------------------------------------------------------

const MIN_SYSTEM_DISTANCE = 0.12;
const MAX_PLACEMENT_RETRIES = 50;

/**
 * v4M2.2 — Even generator. Preserves v1 behaviour exactly:
 * uniform-random positions in the 0.05..0.95 inset with minimum
 * spacing rejection. Adjacency null (lets the legacy MST run).
 * Homeworld preferences null (legacy index-spread).
 */
function generateEven(state: GameState, systemCount: number): ScenarioGalaxy {
  const positions: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < systemCount; i++) {
    positions.push(pickPositionMinDist(state, positions));
  }
  return {
    positions,
    flags: positions.map(() => null),
    adjacency: null,
    homeworldPreferences: null,
  };
}

/**
 * v4M2.2 — common positional draw with minimum-distance rejection.
 * Used by Even and as the fallback inner step inside the Bottleneck
 * cluster + Core-and-Rim ring fills. The MFix6.6 spacing rule
 * (`MIN_SYSTEM_DISTANCE = 0.12`) is preserved across shapes so
 * star sprites don't visually overlap.
 */
function pickPositionMinDist(
  state: GameState,
  placed: ReadonlyArray<{ x: number; y: number }>,
  minX: number = 0.05,
  maxX: number = 0.95,
  minY: number = 0.05,
  maxY: number = 0.95,
): { x: number; y: number } {
  const minDistSq = MIN_SYSTEM_DISTANCE * MIN_SYSTEM_DISTANCE;
  let candidate = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  for (let attempt = 0; attempt < MAX_PLACEMENT_RETRIES; attempt += 1) {
    candidate = {
      x: minX + nextFloat(state.rng) * (maxX - minX),
      y: minY + nextFloat(state.rng) * (maxY - minY),
    };
    let ok = true;
    for (const other of placed) {
      const dx = candidate.x - other.x;
      const dy = candidate.y - other.y;
      if (dx * dx + dy * dy < minDistSq) {
        ok = false;
        break;
      }
    }
    if (ok) return candidate;
  }
  return candidate;
}

// ---------------------------------------------------------------------------
// Spiral — logarithmic-spiral arms with hub mesh
// ---------------------------------------------------------------------------

/**
 * v4M2.2 — Spiral generator. K arms scaling with system count
 * (3 / 4 / 5 per §3.8.2). Each system lands on one arm at a
 * parametric `t` along the curve `r = a × exp(b × θ)`. Systems
 * distribute evenly across arms; remainder lands on lowest-indexed
 * arms.
 *
 * Adjacency: arm-chains (inward → outward) + hub mesh (all arm
 * innermost systems pairwise linked).
 *
 * Homeworlds: one per arm, randomised per seed which empire gets
 * which arm (per L2). Empire `e` lands on the OUTERMOST system of
 * the arm assigned to it — empires start on arm tips and race
 * inward toward the contested hub.
 */
function generateSpiral(state: GameState, systemCount: number): ScenarioGalaxy {
  // Arm count auto-scale per §3.8.2.
  const armCount = systemCount <= 10 ? 3 : systemCount <= 20 ? 4 : 5;

  // Distribute systems across arms. Remainder rides on lowest-indexed
  // arms — arm 0 may have one more than arm K-1.
  const perArm: number[] = [];
  const base = Math.floor(systemCount / armCount);
  const remainder = systemCount % armCount;
  for (let k = 0; k < armCount; k++) {
    perArm.push(base + (k < remainder ? 1 : 0));
  }

  // Spiral curve constants — tuned so arms fit within 0..0.45 radius
  // band with visible curl. `a` is the starting radius at θ=0; `b`
  // controls the tightness (smaller = tighter coil). Tuned for
  // visual readability against the existing galaxy view zoom.
  const A = 0.05;
  const B = 0.18;
  // Arc-length factor: how far each system steps along its arm's
  // parametric `t`. With 10 systems on an arm, t ranges 0..1.
  // Slightly compressed so the outermost system stays inside r=0.45.
  const MAX_T = 0.95;

  const positions: Array<{ x: number; y: number }> = [];
  // Track each system's (armIndex, posInArm) for adjacency wiring.
  const armOf: number[] = [];
  const posOnArm: number[] = [];

  for (let k = 0; k < armCount; k++) {
    const armCountK = perArm[k]!;
    const armBaseTheta = (k / armCount) * Math.PI * 2;
    for (let i = 0; i < armCountK; i++) {
      // Parametric t in [0, MAX_T]. With armCountK==1 we'd divide
      // by 0; clamp by using the midpoint.
      const t = armCountK === 1 ? 0.5 : (i / (armCountK - 1)) * MAX_T;
      const theta = armBaseTheta + t * Math.PI * 2;
      const r = A * Math.exp(B * t * Math.PI * 2);
      positions.push({
        x: 0.5 + r * Math.cos(theta),
        y: 0.5 + r * Math.sin(theta),
      });
      armOf.push(k);
      posOnArm.push(i);
    }
  }

  // Build adjacency.
  //   1. Arm chain — system i on arm k links to system i±1 on arm k.
  //   2. Hub mesh — every arm's innermost system (posOnArm === 0)
  //      links to every other arm's innermost system.
  const adjacency: Array<readonly [number, number]> = [];
  const innermost: number[] = [];
  for (let i = 0; i < positions.length; i++) {
    if (posOnArm[i] === 0) innermost.push(i);
    // Find the next system on the same arm (if any).
    for (let j = i + 1; j < positions.length; j++) {
      if (armOf[j] !== armOf[i]) continue;
      if (posOnArm[j] === posOnArm[i]! + 1) {
        adjacency.push([i, j]);
        break;
      }
    }
  }
  // Hub mesh.
  for (let i = 0; i < innermost.length; i++) {
    for (let j = i + 1; j < innermost.length; j++) {
      adjacency.push([innermost[i]!, innermost[j]!]);
    }
  }

  // Homeworld preferences — outermost system per arm, then arms
  // randomly permuted by seed for empire-to-arm assignment.
  const outermostPerArm: number[] = new Array(armCount).fill(-1);
  for (let i = 0; i < positions.length; i++) {
    const k = armOf[i]!;
    if (outermostPerArm[k] === -1 || posOnArm[i]! > posOnArm[outermostPerArm[k]!]!) {
      outermostPerArm[k] = i;
    }
  }
  // Fisher-Yates shuffle of the arm order so seed picks which
  // empire gets which arm.
  const armPermutation = [...Array(armCount).keys()];
  for (let i = armPermutation.length - 1; i > 0; i--) {
    const j = nextInt(state.rng, 0, i + 1);
    const tmp = armPermutation[i]!;
    armPermutation[i] = armPermutation[j]!;
    armPermutation[j] = tmp;
  }
  const homeworldPreferences = armPermutation.map(
    (armIdx) => outermostPerArm[armIdx]!,
  );

  return {
    positions,
    flags: positions.map(() => null),
    adjacency,
    homeworldPreferences,
  };
}

// ---------------------------------------------------------------------------
// Bottleneck — two clusters joined by a single rich choke system
// ---------------------------------------------------------------------------

/**
 * v4M2.2 — Bottleneck generator. Two clusters at `(0.25, 0.5)` and
 * `(0.75, 0.5)`, each within radius ~0.20; choke system at the
 * geometric centre. 50/50 split per (B1), ±1 if odd.
 *
 * Choke system flags: `forceBiomeBias: 'gaia'` (planet-roll weights
 * Gaia heavily) and `forcePlanetCount: 3` (per I1, regardless of the
 * planets-per-system tier).
 *
 * Adjacency: in-cluster nearest-neighbour mesh (k=3) + choke gets
 * exactly two links (one nearest in each cluster). The choke is the
 * sole cross-cluster path per M1.
 *
 * Homeworlds: alternate clusters by empire index (D2 deterministic
 * spread). With 2 empires → 1 per cluster; 5 → 3/2 split.
 */
function generateBottleneck(
  state: GameState,
  systemCount: number,
): ScenarioGalaxy {
  // Reserve 1 system for the choke. The remaining N-1 split 50/50.
  const remaining = Math.max(2, systemCount - 1);
  const clusterA = Math.floor(remaining / 2);
  const clusterB = remaining - clusterA;

  const positions: Array<{ x: number; y: number }> = [];
  const clusterOf: number[] = []; // 0 = A, 1 = B, 2 = choke

  // Cluster A — left side, centred at (0.25, 0.5).
  for (let i = 0; i < clusterA; i++) {
    const placed = positions.filter((_, idx) => clusterOf[idx] === 0);
    positions.push(pickInCluster(state, placed, 0.25, 0.5, 0.18));
    clusterOf.push(0);
  }
  // Cluster B — right side, centred at (0.75, 0.5).
  for (let i = 0; i < clusterB; i++) {
    const placed = positions.filter((_, idx) => clusterOf[idx] === 1);
    positions.push(pickInCluster(state, placed, 0.75, 0.5, 0.18));
    clusterOf.push(1);
  }
  // Choke — exact centre.
  const chokeIdx = positions.length;
  positions.push({ x: 0.5, y: 0.5 });
  clusterOf.push(2);

  // Flags — only the choke carries overrides.
  const flags: Array<SystemFlags | null> = positions.map(() => null);
  flags[chokeIdx] = { forceBiomeBias: 'gaia', forcePlanetCount: 3 };

  // Adjacency: 3-nearest-neighbour mesh within each cluster, then
  // the choke gets exactly one link to the nearest cluster-A system
  // and one to the nearest cluster-B system.
  const adjacency: Array<readonly [number, number]> = [];
  addKNearestMesh(positions, clusterOf, 0, adjacency);
  addKNearestMesh(positions, clusterOf, 1, adjacency);
  const nearestA = nearestInCluster(positions, clusterOf, chokeIdx, 0);
  const nearestB = nearestInCluster(positions, clusterOf, chokeIdx, 1);
  if (nearestA >= 0) adjacency.push([chokeIdx, nearestA]);
  if (nearestB >= 0) adjacency.push([chokeIdx, nearestB]);

  // Homeworld preferences — alternate clusters per empire index.
  // Each cluster's "preferred" system is the one furthest from the
  // choke — back-line starting position, classic.
  const aSystems = positions
    .map((_, i) => i)
    .filter((i) => clusterOf[i] === 0);
  const bSystems = positions
    .map((_, i) => i)
    .filter((i) => clusterOf[i] === 1);
  // Sort each cluster by distance from choke, descending — far first.
  const distFromChoke = (i: number): number => {
    const p = positions[i]!;
    const dx = p.x - 0.5;
    const dy = p.y - 0.5;
    return dx * dx + dy * dy;
  };
  aSystems.sort((p, q) => distFromChoke(q) - distFromChoke(p));
  bSystems.sort((p, q) => distFromChoke(q) - distFromChoke(p));
  // Build the alternation order. Seed-shuffle which cluster goes first.
  const aFirst = nextFloat(state.rng) < 0.5;
  const order: number[] = [];
  let ai = 0;
  let bi = 0;
  let pickA = aFirst;
  while (ai < aSystems.length || bi < bSystems.length) {
    if (pickA && ai < aSystems.length) {
      order.push(aSystems[ai]!);
      ai++;
    } else if (!pickA && bi < bSystems.length) {
      order.push(bSystems[bi]!);
      bi++;
    } else if (ai < aSystems.length) {
      order.push(aSystems[ai]!);
      ai++;
    } else if (bi < bSystems.length) {
      order.push(bSystems[bi]!);
      bi++;
    }
    pickA = !pickA;
  }

  return {
    positions,
    flags,
    adjacency,
    homeworldPreferences: order,
  };
}

/** Bottleneck helper — draw a position inside a circular cluster. */
function pickInCluster(
  state: GameState,
  placed: ReadonlyArray<{ x: number; y: number }>,
  cx: number,
  cy: number,
  radius: number,
): { x: number; y: number } {
  const minDistSq = MIN_SYSTEM_DISTANCE * MIN_SYSTEM_DISTANCE;
  let candidate = { x: cx, y: cy };
  for (let attempt = 0; attempt < MAX_PLACEMENT_RETRIES; attempt += 1) {
    const r = Math.sqrt(nextFloat(state.rng)) * radius; // uniform-area inside disc
    const theta = nextFloat(state.rng) * Math.PI * 2;
    candidate = {
      x: cx + r * Math.cos(theta),
      y: cy + r * Math.sin(theta),
    };
    let ok = true;
    for (const other of placed) {
      const dx = candidate.x - other.x;
      const dy = candidate.y - other.y;
      if (dx * dx + dy * dy < minDistSq) {
        ok = false;
        break;
      }
    }
    if (ok) return candidate;
  }
  return candidate;
}

// ---------------------------------------------------------------------------
// Core and Rim — concentric layout with dense inner Core, sparser Rim
// ---------------------------------------------------------------------------

/**
 * v4M2.2 — Core-and-Rim generator. Concentric layout per §3.8.4:
 *   - Core disc: centre (0.5, 0.5), radius 0..0.30, area ≈ 0.283.
 *   - Rim annulus: inner radius 0.30, outer radius 0.45, area ≈ 0.353.
 *   - System density ratio 2:1 (Core packs 2× per unit area).
 *
 * Count split lands at ≈62% Core / 38% Rim — the Core holds the
 * bulk despite being smaller in area.
 *
 * Adjacency: 3-NN mesh within Core, 2-NN sparser mesh within Rim,
 * plus gateway crossings (every Rim system within ~0.05 frame-units
 * of the boundary gets a link to its nearest Core system).
 *
 * Homeworlds: all empires start in the Rim per (K1). Rim systems
 * sort by polar angle so empires distribute evenly around the ring.
 */
function generateCoreAndRim(
  state: GameState,
  systemCount: number,
): ScenarioGalaxy {
  // Count split — 62% Core / 38% Rim, rounded so they sum to N.
  const coreCount = Math.max(2, Math.round(systemCount * 0.62));
  const rimCount = systemCount - coreCount;

  const positions: Array<{ x: number; y: number }> = [];
  const region: number[] = []; // 0 = Core, 1 = Rim

  // Fill Core (disc, r=0..0.30 around 0.5, 0.5).
  for (let i = 0; i < coreCount; i++) {
    const placed = positions.filter((_, idx) => region[idx] === 0);
    positions.push(pickInDisc(state, placed, 0.5, 0.5, 0.30));
    region.push(0);
  }
  // Fill Rim (annulus r=0.30..0.45).
  for (let i = 0; i < rimCount; i++) {
    const placed = positions.filter((_, idx) => region[idx] === 1);
    positions.push(pickInAnnulus(state, placed, 0.5, 0.5, 0.30, 0.45));
    region.push(1);
  }

  // Adjacency — 3-NN mesh within Core, 2-NN within Rim, gateway
  // crossings linking each Rim system to its nearest Core system.
  //
  // GALAXY.md §3.8.4 originally framed gateways as "every Rim
  // system within ~0.05 of the boundary"; in practice that
  // disconnects the whole Rim from the Core when low system counts
  // leave no Rim systems in the [0.30, 0.35] band. Relaxed to "every
  // Rim system gets exactly one gateway to its nearest Core
  // system." Still reads as a handful of crossings (Rim is ~38% of
  // N), still preserves the contested-border feel — but the graph
  // can't fragment on a small-N seed.
  const adjacency: Array<readonly [number, number]> = [];
  addKNearestMeshK(positions, region, 0, adjacency, 3);
  addKNearestMeshK(positions, region, 1, adjacency, 2);
  for (let i = 0; i < positions.length; i++) {
    if (region[i] !== 1) continue;
    const nearestCore = nearestInCluster(positions, region, i, 0);
    if (nearestCore >= 0) {
      adjacency.push([i, nearestCore]);
    }
  }

  // Homeworld preferences — Rim systems sorted by polar angle, then
  // empire-to-angle assignment maximally spread. Two empires →
  // 180° apart; five empires → 72° apart, etc.
  const rimIndices = positions
    .map((_, i) => i)
    .filter((i) => region[i] === 1);
  rimIndices.sort((a, b) => {
    const pa = positions[a]!;
    const pb = positions[b]!;
    const angleA = Math.atan2(pa.y - 0.5, pa.x - 0.5);
    const angleB = Math.atan2(pb.y - 0.5, pb.x - 0.5);
    return angleA - angleB;
  });

  return {
    positions,
    flags: positions.map(() => null),
    adjacency,
    homeworldPreferences: rimIndices,
  };
}

/** Disc sampler with min-distance rejection. */
function pickInDisc(
  state: GameState,
  placed: ReadonlyArray<{ x: number; y: number }>,
  cx: number,
  cy: number,
  radius: number,
): { x: number; y: number } {
  const minDistSq = MIN_SYSTEM_DISTANCE * MIN_SYSTEM_DISTANCE;
  let candidate = { x: cx, y: cy };
  for (let attempt = 0; attempt < MAX_PLACEMENT_RETRIES; attempt += 1) {
    const r = Math.sqrt(nextFloat(state.rng)) * radius;
    const theta = nextFloat(state.rng) * Math.PI * 2;
    candidate = { x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) };
    let ok = true;
    for (const other of placed) {
      const dx = candidate.x - other.x;
      const dy = candidate.y - other.y;
      if (dx * dx + dy * dy < minDistSq) {
        ok = false;
        break;
      }
    }
    if (ok) return candidate;
  }
  return candidate;
}

/** Annulus sampler — uniform area between inner / outer radius. */
function pickInAnnulus(
  state: GameState,
  placed: ReadonlyArray<{ x: number; y: number }>,
  cx: number,
  cy: number,
  rInner: number,
  rOuter: number,
): { x: number; y: number } {
  const minDistSq = MIN_SYSTEM_DISTANCE * MIN_SYSTEM_DISTANCE;
  let candidate = { x: cx + rInner, y: cy };
  for (let attempt = 0; attempt < MAX_PLACEMENT_RETRIES; attempt += 1) {
    // Uniform-area in an annulus = sqrt(uniform(rIn², rOut²)).
    const u = nextFloat(state.rng);
    const r = Math.sqrt(u * (rOuter * rOuter - rInner * rInner) + rInner * rInner);
    const theta = nextFloat(state.rng) * Math.PI * 2;
    candidate = { x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) };
    let ok = true;
    for (const other of placed) {
      const dx = candidate.x - other.x;
      const dy = candidate.y - other.y;
      if (dx * dx + dy * dy < minDistSq) {
        ok = false;
        break;
      }
    }
    if (ok) return candidate;
  }
  return candidate;
}

// ---------------------------------------------------------------------------
// Adjacency helpers
// ---------------------------------------------------------------------------

/**
 * Add 3-nearest-neighbour mesh edges within the systems belonging
 * to a given cluster/region. Dedup: each edge added once
 * regardless of which endpoint enumerated it first.
 */
function addKNearestMesh(
  positions: ReadonlyArray<{ x: number; y: number }>,
  region: ReadonlyArray<number>,
  targetRegion: number,
  adjacency: Array<readonly [number, number]>,
): void {
  addKNearestMeshK(positions, region, targetRegion, adjacency, 3);
}

/** Parameterised version — k controls the nearest-neighbour count. */
function addKNearestMeshK(
  positions: ReadonlyArray<{ x: number; y: number }>,
  region: ReadonlyArray<number>,
  targetRegion: number,
  adjacency: Array<readonly [number, number]>,
  k: number,
): void {
  const indices = positions
    .map((_, i) => i)
    .filter((i) => region[i] === targetRegion);
  const seen = new Set<string>();
  for (const i of indices) {
    const sorted = indices
      .filter((j) => j !== i)
      .map((j) => ({
        j,
        d: distSq(positions[i]!, positions[j]!),
      }))
      .sort((a, b) => a.d - b.d)
      .slice(0, k);
    for (const entry of sorted) {
      const lo = Math.min(i, entry.j);
      const hi = Math.max(i, entry.j);
      const key = `${lo}-${hi}`;
      if (seen.has(key)) continue;
      seen.add(key);
      adjacency.push([lo, hi]);
    }
  }
}

/** Nearest system in a given region to a given source index. */
function nearestInCluster(
  positions: ReadonlyArray<{ x: number; y: number }>,
  region: ReadonlyArray<number>,
  fromIdx: number,
  targetRegion: number,
): number {
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < positions.length; i++) {
    if (i === fromIdx) continue;
    if (region[i] !== targetRegion) continue;
    const d = distSq(positions[i]!, positions[fromIdx]!);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

function distSq(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}
